import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AdminTopBar,
  ViewScroll,
  Button,
  IconButton,
  Avatar,
  Sym,
  RolePill,
  StatusPill,
  SectionLabel,
  MEMBER_STATUS_META,
  type MemberStatus,
} from '../../ui';
import { ContentToolbar } from '../../ui/ContentToolbar';
import { LoadingSpinner, PageError } from '../../components/LoadingSpinner';
import { fetchUsers } from '../../api/users';
import { fetchFarms } from '../../api/farms';
import { fetchDevices } from '../../api/devices';
import { fetchAssignments, createAssignment, deleteAssignment } from '../../api/assignments';
import { UserRole, DeviceStatus, type UserInfo, type FarmInfo, type DeviceInfo } from '@grosync/common';

/* ============================================================================
 * 담당자관리 — 담당자(OPERATOR) ↔ 사용자 연결 + 담당 농장·장비 조회.
 *
 * 담당자-사용자 연결 관계는 백엔드 assignments API(/api/assignments)에서 가져온다.
 * 응답 배열을 { [userId]: managerId } 맵으로 환원해 사용한다.
 *
 * 도메인 매핑:
 *  - 담당자(managers): role === OPERATOR 인 사용자
 *  - 연결 가능 사용자(linkable): role === VIEWER 등 그 외 사용자
 *  - 담당 범위(scope): 연결된 사용자들이 소유한 농장(ownerId) + 해당 농장의 장비/경고에서 파생
 * ========================================================================= */

type LinkMap = Record<string, string>;

interface UserView {
  id: string;
  name: string;
  phone: string;
  region: string;
  status: MemberStatus;
  farms: FarmView[];
  deviceCount: number;
}

interface FarmView {
  id: string;
  name: string;
  region: string;
  devices: number;
  warns: number;
  status: 'ok' | 'warn' | 'off';
}

interface ManagerView {
  id: string;
  name: string;
  phone: string;
  region: string;
}

interface Scope {
  users: UserView[];
  farmCount: number;
  deviceCount: number;
  warns: number;
}

/* ---- helpers ----------------------------------------------------------- */

const toMemberStatus = (u: UserInfo): MemberStatus => (u.isActive === false ? 'dormant' : 'active');

const toUiStatus = (s: DeviceStatus): 'ok' | 'warn' | 'off' => {
  switch (s) {
    case DeviceStatus.ACTIVE:
      return 'ok';
    case DeviceStatus.WARNING:
    case DeviceStatus.ERROR:
      return 'warn';
    default:
      return 'off';
  }
};

/** 소유 사용자별 농장 + 장비/경고 집계 화면 모델 */
function buildUserViews(
  users: UserInfo[],
  farms: FarmInfo[],
  devices: DeviceInfo[],
): UserView[] {
  const devsByFarm = new Map<string, DeviceInfo[]>();
  for (const d of devices) {
    if (d.farmId) {
      const list = devsByFarm.get(d.farmId);
      if (list) list.push(d);
      else devsByFarm.set(d.farmId, [d]);
    }
  }

  return users.map((u) => {
    const ownedFarms = farms.filter((f) => f.ownerId === u.id);
    const farmViews: FarmView[] = ownedFarms.map((f) => {
      const ds = devsByFarm.get(f.id) ?? [];
      const uiStatuses = ds.map((d) => toUiStatus(d.status));
      const warns = uiStatuses.filter((s) => s === 'warn').length;
      const status: 'ok' | 'warn' | 'off' = warns > 0
        ? 'warn'
        : ds.length === 0 || uiStatuses.every((s) => s === 'off')
          ? 'off'
          : 'ok';
      return {
        id: f.id,
        name: f.name,
        region: f.location?.description ?? '-',
        devices: ds.length || f.deviceCount || 0,
        warns,
        status,
      };
    });
    return {
      id: u.id,
      name: u.name,
      phone: u.phone ?? '-',
      region: farmViews[0]?.region ?? '-',
      status: toMemberStatus(u),
      farms: farmViews,
      deviceCount: farmViews.reduce((n, f) => n + f.devices, 0),
    };
  });
}

function scopeOf(managerId: string, links: LinkMap, userViews: UserView[]): Scope {
  const users = userViews.filter((u) => links[u.id] === managerId);
  const farms = users.flatMap((u) => u.farms);
  return {
    users,
    farmCount: farms.length,
    deviceCount: farms.reduce((n, f) => n + f.devices, 0),
    warns: farms.reduce((n, f) => n + f.warns, 0),
  };
}

/* ---- ScopeStat --------------------------------------------------------- */

function ScopeStat({
  icon,
  label,
  value,
  warn,
}: {
  icon: string;
  label: string;
  value: string;
  warn?: boolean;
}): React.ReactElement {
  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 12,
        background: 'var(--md-sys-color-surface-container)',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          font: '500 11px/14px var(--gs-font-kr)',
          color: 'var(--md-sys-color-on-surface-variant)',
        }}
      >
        <Sym name={icon} size={13} style={{ color: warn ? 'var(--gs-warning)' : 'var(--md-sys-color-primary)' }} />
        {label}
      </span>
      <span
        className="gs-num"
        style={{
          font: '700 18px/20px var(--gs-font-kr)',
          letterSpacing: -0.3,
          color: warn ? 'var(--gs-warning)' : 'var(--md-sys-color-on-surface)',
        }}
      >
        {value}
      </span>
    </div>
  );
}

/* ---- ManagerCard ------------------------------------------------------- */

function ManagerCard({
  m,
  scope,
  onOpen,
}: {
  m: ManagerView;
  scope: Scope;
  onOpen: () => void;
}): React.ReactElement {
  return (
    <div
      className="gs-row"
      onClick={onOpen}
      style={{
        background: 'var(--md-sys-color-surface-container-lowest)',
        border: '1px solid var(--md-sys-color-outline-variant)',
        borderRadius: 18,
        padding: 18,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Avatar
          name={m.name}
          size={48}
          color="var(--md-sys-color-secondary-container)"
          fg="var(--md-sys-color-on-secondary-container)"
          style={{ font: '600 19px/1 var(--gs-font-kr)' }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ font: '700 16px/21px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
              {m.name}
            </span>
            <RolePill role="manager" />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              marginTop: 3,
              font: '400 12px/16px var(--gs-font-kr)',
              color: 'var(--md-sys-color-on-surface-variant)',
            }}
          >
            <Sym name="location_on" size={13} />
            {m.region}
          </div>
        </div>
        {scope.warns > 0 && (
          <span className="gs-tag gs-tag-warn">
            <Sym name="warning" size={12} fill={1} />
            {scope.warns}
          </span>
        )}
      </div>

      {/* connected user avatar stack (max 5) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px',
          borderRadius: 12,
          background: 'var(--md-sys-color-surface-container-low)',
        }}
      >
        <div style={{ display: 'flex' }}>
          {scope.users.slice(0, 5).map((u, i) => (
            <div
              key={u.id}
              style={{
                marginLeft: i ? -8 : 0,
                borderRadius: '50%',
                border: '2px solid var(--md-sys-color-surface-container-low)',
              }}
            >
              <Avatar name={u.name} size={28} color="var(--gs-secondary-90)" style={{ font: '600 12px/1 var(--gs-font-kr)' }} />
            </div>
          ))}
          {!scope.users.length && (
            <span style={{ font: '400 12px/28px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
              연결된 사용자 없음
            </span>
          )}
        </div>
        <span style={{ flex: 1 }} />
        <span style={{ font: '600 13px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
          담당 사용자{' '}
          <span className="gs-num" style={{ color: 'var(--md-sys-color-primary)' }}>
            {scope.users.length}
          </span>
          명
        </span>
      </div>

      {/* scope stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <ScopeStat icon="agriculture" label="담당 농장" value={`${scope.farmCount}곳`} />
        <ScopeStat icon="sensors" label="담당 장비" value={`${scope.deviceCount}대`} />
        <ScopeStat icon="warning" label="경고" value={`${scope.warns}건`} warn={scope.warns > 0} />
      </div>
    </div>
  );
}

/* ---- UserLinkRow ------------------------------------------------------- */

function UserLinkRow({ u, onDisconnect }: { u: UserView; onDisconnect: (id: string) => void }): React.ReactElement {
  return (
    <div
      style={{
        padding: '12px 14px',
        borderRadius: 14,
        background: 'var(--md-sys-color-surface-container-low)',
        border: '1px solid var(--md-sys-color-outline-variant)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar name={u.name} size={36} color="var(--gs-secondary-90)" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ font: '600 14px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
              {u.name}
            </span>
            <StatusPill s={u.status} meta={MEMBER_STATUS_META} />
          </div>
          <div style={{ marginTop: 2, font: '400 11px/14px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
            {u.region} · 농장 {u.farms.length} · 장비 {u.deviceCount}대
          </div>
        </div>
        <IconButton
          icon="link_off"
          size={18}
          color="var(--gs-error)"
          title="연결 해제"
          onClick={(e) => {
            e.stopPropagation();
            onDisconnect(u.id);
          }}
          style={{ width: 32, height: 32 }}
        />
      </div>

      {/* user's farms (담당자가 조회 가능한 농장·장비) */}
      {u.farms.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {u.farms.map((f) => (
            <div
              key={f.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 10,
                background: 'var(--md-sys-color-surface-container)',
              }}
            >
              <Sym name="agriculture" size={16} fill={1} style={{ color: 'var(--md-sys-color-primary)' }} />
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  font: '500 12px/16px var(--gs-font-kr)',
                  color: 'var(--md-sys-color-on-surface)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {f.name}
              </span>
              <span style={{ font: '400 11px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                장비 {f.devices}
              </span>
              <StatusPill s={f.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- ManagerDrawer ----------------------------------------------------- */

function ManagerDrawer({
  m,
  scope,
  available,
  onConnect,
  onDisconnect,
  onClose,
}: {
  m: ManagerView | null;
  scope: Scope;
  available: UserView[];
  onConnect: (userId: string, managerId: string) => void;
  onDisconnect: (userId: string) => void;
  onClose: () => void;
}): React.ReactElement | null {
  const [picking, setPicking] = useState(false);
  if (!m) return null;
  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.32)', zIndex: 20 }}
      />
      <aside
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 440,
          zIndex: 21,
          background: 'var(--md-sys-color-surface)',
          borderLeft: '1px solid var(--md-sys-color-outline-variant)',
          boxShadow: '-12px 0 40px -12px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'drawerIn 240ms cubic-bezier(.2,0,0,1)',
        }}
      >
        {/* header */}
        <div
          style={{
            padding: '18px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            borderBottom: '1px solid var(--md-sys-color-outline-variant)',
          }}
        >
          <span style={{ flex: 1, font: '700 16px/22px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
            담당자 상세
          </span>
          <IconButton icon="close" size={22} onClick={onClose} />
        </div>

        <div
          className="gs-scroll-hide"
          style={{ flex: 1, overflowY: 'auto', padding: '22px 20px', display: 'flex', flexDirection: 'column', gap: 22 }}
        >
          {/* profile */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Avatar
              name={m.name}
              size={60}
              color="var(--md-sys-color-secondary-container)"
              fg="var(--md-sys-color-on-secondary-container)"
              style={{ font: '600 23px/1 var(--gs-font-kr)' }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ font: '700 20px/26px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
                  {m.name}
                </span>
                <RolePill role="manager" />
              </div>
              <div
                className="gs-num"
                style={{ marginTop: 4, font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}
              >
                {m.phone} · {m.region}
              </div>
            </div>
          </div>

          {/* scope summary — 4 stats */}
          <div>
            <SectionLabel style={{ marginBottom: 10 }}>
              담당 범위{' '}
              <span style={{ color: 'var(--md-sys-color-on-surface-variant)', fontWeight: 400 }}>
                · 연결된 사용자의 농장·장비
              </span>
            </SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
              <ScopeStat icon="group" label="사용자" value={`${scope.users.length}명`} />
              <ScopeStat icon="agriculture" label="농장" value={`${scope.farmCount}곳`} />
              <ScopeStat icon="sensors" label="장비" value={`${scope.deviceCount}대`} />
              <ScopeStat icon="warning" label="경고" value={`${scope.warns}`} warn={scope.warns > 0} />
            </div>
          </div>

          {/* connected users */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              <SectionLabel style={{ flex: 1 }}>연결된 사용자 · {scope.users.length}</SectionLabel>
              <Button
                variant={picking ? 'tonal' : 'filled'}
                icon={picking ? 'close' : 'person_add'}
                onClick={() => setPicking((p) => !p)}
                style={{ height: 32, padding: '0 12px', font: '600 12px/1 var(--gs-font-kr)' }}
              >
                {picking ? '닫기' : '사용자 연결'}
              </Button>
            </div>

            {/* picker */}
            {picking && (
              <div
                style={{
                  marginBottom: 12,
                  padding: 8,
                  borderRadius: 14,
                  background: 'var(--md-sys-color-surface-container-low)',
                  border: '1px dashed var(--md-sys-color-outline)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                {available.length ? (
                  available.map((u) => (
                    <div
                      key={u.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 10px',
                        borderRadius: 10,
                        background: 'var(--md-sys-color-surface-container-lowest)',
                      }}
                    >
                      <Avatar name={u.name} size={30} color="var(--gs-secondary-90)" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ font: '600 13px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
                          {u.name}
                        </div>
                        <div style={{ font: '400 11px/14px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                          {u.region} · 농장 {u.farms.length}
                        </div>
                      </div>
                      <Button
                        variant="filled"
                        onClick={() => onConnect(u.id, m.id)}
                        style={{ height: 30, padding: '0 14px', font: '600 12px/1 var(--gs-font-kr)' }}
                      >
                        연결
                      </Button>
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      padding: 14,
                      textAlign: 'center',
                      font: '400 12px/16px var(--gs-font-kr)',
                      color: 'var(--md-sys-color-on-surface-variant)',
                    }}
                  >
                    연결 가능한 미배정 사용자가 없습니다
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {scope.users.length ? (
                scope.users.map((u) => <UserLinkRow key={u.id} u={u} onDisconnect={onDisconnect} />)
              ) : (
                <div
                  style={{
                    padding: 20,
                    borderRadius: 14,
                    textAlign: 'center',
                    background: 'var(--md-sys-color-surface-container-low)',
                    font: '400 13px/18px var(--gs-font-kr)',
                    color: 'var(--md-sys-color-on-surface-variant)',
                  }}
                >
                  연결된 사용자가 없습니다 · "사용자 연결"로 추가하세요
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

/* ---- View -------------------------------------------------------------- */

export default function ManagersView(): React.ReactElement {
  const [q, setQ] = useState('');
  const [selId, setSelId] = useState<string | null>(null);
  const qc = useQueryClient();

  const usersQ = useQuery({ queryKey: ['users'], queryFn: fetchUsers });
  const farmsQ = useQuery({ queryKey: ['farms'], queryFn: fetchFarms });
  const devicesQ = useQuery({ queryKey: ['devices'], queryFn: fetchDevices });
  const assignmentsQ = useQuery({ queryKey: ['assignments'], queryFn: fetchAssignments });

  // 배정 배열 → { [userId]: managerId } 맵
  const links = useMemo<LinkMap>(() => {
    const map: LinkMap = {};
    for (const a of assignmentsQ.data ?? []) map[a.userId] = a.managerId;
    return map;
  }, [assignmentsQ.data]);

  const invalidateAssignments = (): void => {
    void qc.invalidateQueries({ queryKey: ['assignments'] });
  };

  const connectM = useMutation({
    mutationFn: ({ userId, managerId }: { userId: string; managerId: string }) =>
      createAssignment({ userId, managerId }),
    onSuccess: invalidateAssignments,
  });
  const disconnectM = useMutation({
    mutationFn: (userId: string) => deleteAssignment(userId),
    onSuccess: invalidateAssignments,
  });

  const connect = (userId: string, managerId: string): void => {
    connectM.mutate({ userId, managerId });
  };
  const disconnect = (userId: string): void => {
    disconnectM.mutate(userId);
  };

  const managers = useMemo<ManagerView[]>(() => {
    if (!usersQ.data) return [];
    return usersQ.data
      .filter((u) => u.role === UserRole.OPERATOR)
      .map((u) => ({
        id: u.id,
        name: u.name,
        phone: u.phone ?? '-',
        region: '-', // 사용자는 지역 컬럼이 없어 표기 생략
      }));
  }, [usersQ.data]);

  // 연결 가능한(담당자가 아닌) 사용자만 화면 모델로 구성
  const userViews = useMemo<UserView[]>(() => {
    if (!usersQ.data) return [];
    const linkable = usersQ.data.filter((u) => u.role !== UserRole.OPERATOR);
    return buildUserViews(linkable, farmsQ.data ?? [], devicesQ.data ?? []);
  }, [usersQ.data, farmsQ.data, devicesQ.data]);

  const scopeByManager = useMemo<Map<string, Scope>>(() => {
    const map = new Map<string, Scope>();
    for (const m of managers) map.set(m.id, scopeOf(m.id, links, userViews));
    return map;
  }, [managers, links, userViews]);

  const emptyScope = useMemo<Scope>(() => ({ users: [], farmCount: 0, deviceCount: 0, warns: 0 }), []);

  const totalManaged = useMemo(
    () => userViews.filter((u) => links[u.id]).length,
    [userViews, links],
  );
  const unassigned = useMemo(
    () => userViews.filter((u) => !links[u.id]).length,
    [userViews, links],
  );

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return managers.filter((m) => !needle || [m.name, m.region].some((s) => s.toLowerCase().includes(needle)));
  }, [managers, q]);

  if (usersQ.isLoading) return <LoadingSpinner />;
  if (usersQ.error) return <PageError message={(usersQ.error as Error).message} />;

  const sel = managers.find((m) => m.id === selId) ?? null;
  const selScope = sel ? (scopeByManager.get(sel.id) ?? emptyScope) : emptyScope;
  const available = userViews.filter((u) => !links[u.id]); // 미배정 사용자

  return (
    <>
      <AdminTopBar
        title="담당자관리"
        subtitle={`담당자 ${managers.length}명 · 담당 사용자 ${totalManaged}명 · 미배정 ${unassigned}명`}
      />

      <ViewScroll gap={18}>
        <ContentToolbar
          search={q}
          onSearch={setQ}
          searchPlaceholder="담당자 이름 검색"
          actions={
            <Button variant="filled" icon="shield_person" style={{ height: 42 }}>
              담당자 지정
            </Button>
          }
        />
        {/* hierarchy hint */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 18px',
            borderRadius: 14,
            background: 'var(--md-sys-color-secondary-container)',
            color: 'var(--md-sys-color-on-secondary-container)',
          }}
        >
          <Sym name="account_tree" size={20} fill={1} />
          <span style={{ font: '500 13px/18px var(--gs-font-kr)' }}>
            담당자에 사용자를 연결하면, 담당자는 연결된 <b>사용자들의 농장·장비</b>를 모두 조회할 수 있습니다.
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 16 }}>
          {rows.map((m) => (
            <ManagerCard
              key={m.id}
              m={m}
              scope={scopeByManager.get(m.id) ?? emptyScope}
              onOpen={() => setSelId(m.id)}
            />
          ))}
        </div>

        {!rows.length && (
          <div
            style={{
              padding: 48,
              textAlign: 'center',
              font: '400 14px/20px var(--gs-font-kr)',
              color: 'var(--md-sys-color-on-surface-variant)',
            }}
          >
            조건에 맞는 담당자가 없습니다
          </div>
        )}
      </ViewScroll>

      <ManagerDrawer
        m={sel}
        scope={selScope}
        available={available}
        onConnect={connect}
        onDisconnect={disconnect}
        onClose={() => setSelId(null)}
      />
    </>
  );
}
